/**
 * 阿里云 OSS 前端直传模块
 *
 * 架构: 前端获取 STS 临时凭证 → ali-oss SDK 直传 → 返回 CDN URL
 */

import OSS from 'ali-oss';
import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';

// STS Token 响应（API 层自动转为 camelCase）
interface STSTokenData {
  accessKeyId: string;
  accessKeySecret: string;
  securityToken: string;
  expiration: string;
  region: string;
  bucket: string;
  pathPrefix: string;
  cdnDomain: string;
  endpoint: string;
}

// OSS 配置响应（API 层自动转为 camelCase）
interface OSSConfigData {
  enabled: boolean;
  cdnDomain: string;
  region: string;
  bucket: string;
}

// 上传选项
export interface OSSUploadOptions {
  /** 子目录，如 products、avatars */
  directory?: string;
  /** 上传进度回调 (0-100) */
  onProgress?: (percent: number) => void;
}

// 上传结果
export interface OSSUploadResult {
  url: string;
  objectKey: string;
}

// --- STS Token 缓存 ---
let cachedToken: STSTokenData | null = null;
let tokenExpireTime = 0;

// 提前 5 分钟刷新 token
const TOKEN_REFRESH_AHEAD_MS = 5 * 60 * 1000;

async function getSTSToken(): Promise<STSTokenData> {
  const now = Date.now();
  if (cachedToken && tokenExpireTime - now > TOKEN_REFRESH_AHEAD_MS) {
    return cachedToken;
  }

  const res = await handleApiResponse(
    unifiedApi.service('erp').get<{ data: STSTokenData }>('/oss/sts-token')
  );
  const token: STSTokenData = (res as any).data?.data ?? (res as any).data;

  cachedToken = token;
  // 解析 ISO 时间
  tokenExpireTime = new Date(token.expiration).getTime();

  return token;
}

// --- OSS 配置缓存 ---
let cachedOSSConfig: OSSConfigData | null = null;

export async function isOSSEnabled(): Promise<boolean> {
  if (cachedOSSConfig !== null) {
    return cachedOSSConfig.enabled;
  }
  // 探测失败必须抛出，不能当成「OSS 未启用」。
  //
  // 原先这里 catch 后返回 false 并把 {enabled:false} 写进模块级缓存：只要首次探测
  // 出任何岔子（网络抖动、token 未就绪），整个会话都会静默降级到本地存储 ——
  // 用户看到「上传成功」，文件却根本没进 OSS，刷新或换台机器就没了。
  const res = await handleApiResponse(
    unifiedApi.service('erp').get<{ data: OSSConfigData }>('/oss/config')
  );
  cachedOSSConfig = (res as any).data?.data ?? (res as any).data;
  return cachedOSSConfig!.enabled;
}

/**
 * 上传文件到 OSS
 */
export async function ossUpload(
  file: File,
  options: OSSUploadOptions = {}
): Promise<OSSUploadResult> {
  const token = await getSTSToken();

  const client = new OSS({
    region: `oss-${token.region}`,
    accessKeyId: token.accessKeyId,
    accessKeySecret: token.accessKeySecret,
    stsToken: token.securityToken,
    bucket: token.bucket,
    endpoint: token.endpoint,
    secure: true,
  });

  // 构建 object key: {pathPrefix}/{directory}/{date}/{uuid}.{ext}
  const dir = options.directory || 'images';
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '/');
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const uuid = crypto.randomUUID().slice(0, 12);
  const objectKey = `${token.pathPrefix}/${dir}/${date}/${uuid}.${ext}`;

  await client.put(objectKey, file, { mime: file.type });
  options.onProgress?.(100);

  // 构建访问 URL。未配置 CDN 时使用后端图片代理，兼容私有读 Bucket。
  let url: string;
  if (token.cdnDomain) {
    const domain = token.cdnDomain.startsWith('http')
      ? token.cdnDomain
      : `https://${token.cdnDomain}`;
    url = `${domain.replace(/\/$/, '')}/${objectKey}`;
  } else {
    url = `/api/v1/oss/image?key=${encodeURIComponent(objectKey)}`;
  }

  return { url, objectKey };
}

/**
 * 将 OSS 直连 URL 转换为后端签名代理 URL
 * 解决私有 Bucket 无法直接访问的问题
 *
 * @example
 * ossProxyUrl('https://coolbuy-dev.oss-cn-beijing.aliyuncs.com/dev/tenant_2/products/xxx.jpg')
 * // => '/api/v1/oss/image?key=dev/tenant_2/products/xxx.jpg'
 */
export function ossProxyUrl(url: string): string {
  if (!url) return url;
  const normalized = url.trim();
  // 非 OSS URL 原样返回（本地上传、CDN、已经是代理URL等）
  if (normalized.startsWith('/api/v1/oss/')) return normalized;
  if (normalized.startsWith('/')) return normalized;
  if (normalized.includes('/uploads/')) return normalized;

  // Historical settings may store the raw OSS object key instead of a URL.
  // Convert image-like keys such as "test/tenant_8/favicon.png" to the
  // backend signing proxy so private buckets and relative-path resolution work.
  const isAbsoluteUrl = /^[a-z][a-z0-9+.-]*:/i.test(normalized);
  const isImageObjectKey =
    !isAbsoluteUrl &&
    normalized.includes('/') &&
    /\.(png|jpe?g|gif|webp|svg|ico)(\?.*)?$/i.test(normalized);
  if (isImageObjectKey) {
    return `/api/v1/oss/image?key=${encodeURIComponent(normalized)}`;
  }

  if (!normalized.includes('.aliyuncs.com/')) return normalized;

  try {
    const parsed = new URL(normalized);
    const objectKey = parsed.pathname.replace(/^\//, '');
    // 只代理我们自己私有桶的路径（路径含 tenant_ 目录）；
    // 外部公开桶（如 o2o-pic）路径不含 tenant_，直接使用原始 URL。
    if (!objectKey.includes('tenant_')) return normalized;
    return `/api/v1/oss/image?key=${encodeURIComponent(objectKey)}`;
  } catch {
    return normalized;
  }
}

/**
 * 获取 OSS 图片缩略图 URL
 * @param url 原图 URL
 * @param w 宽度
 * @param h 高度 (可选，不传则等比缩放)
 */
export function getOSSThumbnail(url: string, w: number, h?: number): string {
  // 旧的本地 URL 不处理
  if (!url || url.includes('/uploads/')) {
    return url;
  }
  const mode = h ? `m_fill,w_${w},h_${h}` : `m_lfit,w_${w}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-oss-process=image/resize,${mode}`;
}

/**
 * 获取 OSS WebP 格式 URL
 */
export function getOSSWebp(url: string): string {
  if (!url || url.includes('/uploads/')) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}x-oss-process=image/format,webp`;
}
